'use client';

// ============================================================================
// SPRINT VIEW COMPONENT
// Comprehensive sprint management with burndown charts, velocity tracking,
// story points, and team capacity planning - comparable to Jira/ClickUp
// Date: November 8, 2025
// ============================================================================

import React, { useState, useEffect, useMemo } from 'react';
import {
  Target,
  Calendar,
  TrendingDown,
  TrendingUp,
  Clock,
  CheckCircle2,
  AlertTriangle,
  Users,
  Plus,
  Settings,
  Play,
  Pause,
  Check,
  X,
  BarChart3,
  Timer,
  Zap
} from 'lucide-react';
import { authService } from '@/lib/authService';
import WorkItemsKanbanBoard from '@/components/WorkItemsKanbanBoard';
import WorkItemModal from '@/components/WorkItemModal';
import WorkItemDetailModal from '@/components/WorkItemDetailModal';
import type { WorkItem } from '@/components/UniversalWorkItems';
import { createLogger } from '@/lib/debugLogger';
import { notify, confirm } from '@/lib/notifications';

const logger = createLogger('SprintView');

interface SprintViewProps {
  permissions: {
    can_view: boolean;
    can_create: boolean;
    can_edit: boolean;
    can_delete: boolean;
    can_admin: boolean;
  };
  currentUserId?: number;
  filterByAssignedUser?: boolean;
}

interface Sprint {
  id: number;
  name: string;
  goal: string;
  status: 'planning' | 'active' | 'completed';
  start_date: string;
  end_date: string;
  total_points: number;
  completed_points: number;
  committed_points: number;
  capacity_points: number;
  velocity: number;
  items?: any[];
  burndown?: any[];
  stats?: {
    total_items: number;
    completed_items: number;
    total_points: number;
    completed_points: number;
    remaining_points: number;
    completion_percentage: number;
  };
}

interface BurndownData {
  date: string;
  ideal: number;
  actual: number;
}

export default function SprintView({ permissions, currentUserId, filterByAssignedUser }: SprintViewProps) {
  const [activeSprint, setActiveSprint] = useState<Sprint | null>(null);
  const [sprintItems, setSprintItems] = useState<WorkItem[]>([]);
  const [burndownData, setBurndownData] = useState<BurndownData[]>([]);
  const [loading, setLoading] = useState(true);
  const [showSprintSettings, setShowSprintSettings] = useState(false);
  const [showCreateSprintModal, setShowCreateSprintModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<WorkItem | null>(null);
  
  // Log for debugging
  useEffect(() => {
    logger.debug('[SprintView] currentUserId:', currentUserId, 'filterByAssignedUser:', filterByAssignedUser);
  }, [currentUserId, filterByAssignedUser]);

  // Load active sprint
  useEffect(() => {
    loadActiveSprint();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadActiveSprint() {
    try {
      setLoading(true);
      const token = authService.getTokens()?.access_token;
      
      if (!token) {
        throw new Error('Authentication required');
      }

      // Load active sprint from work_item_sprints API
      const response = await fetch('/api/v1/work-items/sprints?status=active&is_active=true', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to load sprint');
      }

      const data = await response.json();
      
      logger.debug('[SprintView] Sprints API Response:', data);
      
      if (data.success && data.data && data.data.length > 0) {
        const sprintData = data.data[0]; // Get the first active sprint
        
        // Map API response to Sprint interface
        const sprint: Sprint = {
          id: sprintData.id,
          name: sprintData.name,
          goal: sprintData.goal || '',
          status: sprintData.status,
          start_date: sprintData.start_date,
          end_date: sprintData.end_date,
          total_points: sprintData.total_points || sprintData.committed_points || 0,
          completed_points: sprintData.completed_points || 0,
          committed_points: sprintData.committed_points || 0,
          capacity_points: sprintData.capacity_points || 40,
          velocity: sprintData.velocity || 0
        };

        setActiveSprint(sprint);
        
        // Load sprint items
        await loadSprintItems(sprint.id);
        
        // Load burndown data
        await loadBurndownData(sprint.id);
      } else {
        setActiveSprint(null);
      }
      
    } catch (error) {
      logger.error('Failed to load sprint:', error);
      setActiveSprint(null);
    } finally {
      setLoading(false);
    }
  }

  async function loadSprintItems(sprintId: number) {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      // Load sprint items from the sprint items API
      const response = await fetch(`/api/v1/work-items/sprints/${sprintId}/items`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        logger.debug('[SprintView] Loaded sprint items:', result);
        
        if (result.success && result.data) {
          setSprintItems(result.data);
        }
      }
    } catch (error) {
      logger.error('Failed to load sprint items:', error);
    }
  }

  async function loadBurndownData(sprintId: number) {
    try {
      const token = authService.getTokens()?.access_token;
      if (!token) return;

      // Get sprint details with burndown
      const response = await fetch(`/api/v1/work-items/sprints/${sprintId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          // Update sprint with full details
          if (result.data.stats) {
            setActiveSprint(prev => prev ? {
              ...prev,
              total_points: result.data.stats.total_points,
              completed_points: result.data.stats.completed_points
            } : null);
          }
          
          // Set burndown data
          if (result.data.burndown && result.data.burndown.length > 0) {
            const burndown = result.data.burndown.map((snapshot: any) => ({
              date: snapshot.snapshot_date,
              ideal: snapshot.total_points - (snapshot.total_points * (result.data.burndown.indexOf(snapshot) / result.data.burndown.length)),
              actual: snapshot.remaining_points
            }));
            setBurndownData(burndown);
          } else {
            // Generate ideal burndown if no snapshots yet
            generateBurndownData(result.data);
          }
        }
      }
    } catch (error) {
      logger.error('Failed to load burndown data:', error);
    }
  }

  function generateBurndownData(sprint: Sprint) {
    const start = new Date(sprint.start_date);
    const end = new Date(sprint.end_date);
    const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    const pointsPerDay = sprint.total_points / totalDays;

    const data: BurndownData[] = [];
    for (let i = 0; i <= totalDays; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      
      data.push({
        date: date.toISOString().split('T')[0],
        ideal: Math.max(0, sprint.total_points - (pointsPerDay * i)),
        actual: i <= 7 ? sprint.total_points - (i * 2) : sprint.total_points - sprint.completed_points
      });
    }

    setBurndownData(data);
  }

  const daysRemaining = useMemo(() => {
    if (!activeSprint) return 0;
    const end = new Date(activeSprint.end_date);
    const now = new Date();
    return Math.max(0, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
  }, [activeSprint]);

  const velocity = useMemo(() => {
    if (!activeSprint) return 0;
    const start = new Date(activeSprint.start_date);
    const now = new Date();
    const daysElapsed = Math.ceil((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    return daysElapsed > 0 ? (activeSprint.completed_points / daysElapsed).toFixed(1) : '0.0';
  }, [activeSprint]);

  const completionPercentage = useMemo(() => {
    if (!activeSprint || activeSprint.total_points === 0) return 0;
    return Math.round((activeSprint.completed_points / activeSprint.total_points) * 100);
  }, [activeSprint]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading sprint...</p>
        </div>
      </div>
    );
  }

  if (!activeSprint) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Target className="h-16 w-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Active Sprint</h3>
          <p className="text-gray-600 mb-4">Start a new sprint to begin tracking your team's work</p>
          {permissions.can_create && (
            <button 
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto"
              onClick={() => setShowCreateSprintModal(true)}
            >
              <Play className="h-4 w-4" />
              Start Sprint
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Sprint Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Target className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{activeSprint.name}</h2>
              <p className="text-sm text-gray-600">{activeSprint.goal}</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
              Active
            </span>
            {permissions.can_admin && (
              <>
                <button 
                  onClick={() => setShowSprintSettings(true)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <Settings className="h-5 w-5 text-gray-600" />
                </button>
                <button 
                  onClick={async () => {
                    if (!activeSprint) return;
                    if (!(await confirm({ message: 'Are you sure you want to complete this sprint? Any unfinished items will need to be moved to a new sprint.', variant: 'warning' }))) return;
                    
                    try {
                      const token = authService.getTokens()?.access_token;
                      if (!token) throw new Error('Authentication required');
                      
                      const response = await fetch(`/api/v1/work-items/sprints/${activeSprint.id}`, {
                        method: 'PATCH',
                        headers: {
                          'Authorization': `Bearer ${token}`,
                          'Content-Type': 'application/json'
                        },
                        body: JSON.stringify({
                          status: 'completed',
                          is_active: false,
                          velocity: activeSprint.completed_points
                        })
                      });
                      
                      if (!response.ok) {
                        throw new Error('Failed to complete sprint');
                      }
                      
                      await loadActiveSprint();
                    } catch (error) {
                      logger.error('Failed to complete sprint:', error);
                      notify.error('Failed to complete sprint');
                    }
                  }}
                  className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 flex items-center gap-2 text-sm font-medium"
                >
                  <Check className="h-4 w-4" />
                  Complete Sprint
                </button>
              </>
            )}
          </div>
        </div>

        {/* Sprint Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {/* Story Points */}
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Zap className="h-4 w-4 text-blue-600" />
              <span className="text-xs font-medium text-blue-900">Story Points</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">
              {activeSprint.completed_points}/{activeSprint.total_points}
            </div>
            <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all" 
                style={{ width: `${completionPercentage}%` }}
              ></div>
            </div>
          </div>

          {/* Days Remaining */}
          <div className="bg-orange-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Calendar className="h-4 w-4 text-orange-600" />
              <span className="text-xs font-medium text-orange-900">Days Left</span>
            </div>
            <div className="text-2xl font-bold text-orange-600">{daysRemaining}</div>
            <div className="text-xs text-orange-700 mt-1">
              Ends {new Date(activeSprint.end_date).toLocaleDateString()}
            </div>
          </div>

          {/* Velocity */}
          <div className="bg-green-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-green-600" />
              <span className="text-xs font-medium text-green-900">Velocity</span>
            </div>
            <div className="text-2xl font-bold text-green-600">{velocity}</div>
            <div className="text-xs text-green-700 mt-1">points/day</div>
          </div>

          {/* Team Capacity */}
          <div className="bg-purple-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <Users className="h-4 w-4 text-purple-600" />
              <span className="text-xs font-medium text-purple-900">Capacity</span>
            </div>
            <div className="text-2xl font-bold text-purple-600">
              {activeSprint.completed_points}/{activeSprint.capacity_points}
            </div>
            <div className="text-xs text-purple-700 mt-1">
              {Math.round((activeSprint.completed_points / Math.max(activeSprint.capacity_points, 1)) * 100)}% utilized
            </div>
          </div>

          {/* Completion */}
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle2 className="h-4 w-4 text-gray-600" />
              <span className="text-xs font-medium text-gray-900">Progress</span>
            </div>
            <div className="text-2xl font-bold text-gray-900">{completionPercentage}%</div>
            <div className="text-xs text-gray-600 mt-1">complete</div>
          </div>
        </div>
      </div>

      {/* Burndown Chart */}
      <div className="bg-white border-b border-gray-200 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <TrendingDown className="h-4 w-4" />
          Burndown Chart
        </h3>
        <div className="h-48 bg-gray-50 rounded-lg p-4 relative">
          {/* Simple SVG Burndown Chart */}
          <svg className="w-full h-full" viewBox="0 0 400 150">
            {/* Axes */}
            <line x1="30" y1="10" x2="30" y2="130" stroke="#9CA3AF" strokeWidth="2" />
            <line x1="30" y1="130" x2="390" y2="130" stroke="#9CA3AF" strokeWidth="2" />
            
            {/* Ideal Line */}
            <line 
              x1="30" 
              y1="20" 
              x2="390" 
              y2="130" 
              stroke="#94A3B8" 
              strokeWidth="2" 
              strokeDasharray="5,5"
            />
            
            {/* Actual Line */}
            {burndownData.length > 1 && (
              <polyline
                points={burndownData.map((d, i) => {
                  const x = 30 + (360 * (i / (burndownData.length - 1)));
                  const y = 130 - (110 * (d.actual / (activeSprint?.total_points || 1)));
                  return `${x},${y}`;
                }).join(' ')}
                fill="none"
                stroke="#3B82F6"
                strokeWidth="3"
              />
            )}
            
            {/* Labels */}
            <text x="10" y="25" fontSize="10" fill="#6B7280">
              {activeSprint.total_points}
            </text>
            <text x="10" y="135" fontSize="10" fill="#6B7280">0</text>
            <text x="25" y="145" fontSize="10" fill="#6B7280">Day 1</text>
            <text x="360" y="145" fontSize="10" fill="#6B7280">Day {burndownData.length}</text>
          </svg>
          
          {/* Legend */}
          <div className="absolute bottom-2 right-4 flex gap-4 text-xs">
            <div className="flex items-center gap-1">
              <div className="w-3 h-0.5 bg-gray-400"></div>
              <span className="text-gray-600">Ideal</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-0.5 bg-blue-600"></div>
              <span className="text-gray-600">Actual</span>
            </div>
          </div>
        </div>
      </div>

      {/* Sprint Board */}
      <div className="flex-1 overflow-hidden">
        <WorkItemsKanbanBoard
          items={sprintItems}
          onUpdateStatus={async (itemId, newStatus) => {
            // Update item status
            logger.debug('Update status:', itemId, newStatus);
          }}
          onViewItem={(itemId) => {
            const item = sprintItems.find(i => i.id === itemId);
            if (item) {
              setSelectedItem(item);
              setShowDetailModal(true);
            }
          }}
          itemType="task"
          workflowStates={[
            { key: 'open', label: 'To Do', color: 'gray' },
            { key: 'in_progress', label: 'In Progress', color: 'blue' },
            { key: 'review', label: 'In Review', color: 'yellow' },
            { key: 'closed', label: 'Done', color: 'green' }
          ]}
          moduleKey="total-operations"
          currentUserId={currentUserId}
        />
      </div>

      {/* Work Item Detail Modal */}
      {showDetailModal && selectedItem && (
        <WorkItemDetailModal
          isOpen={showDetailModal}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedItem(null);
          }}
          workItem={selectedItem}
          onRefresh={() => {
            // Refresh sprint items after update
            if (activeSprint) {
              loadSprintItems(activeSprint.id);
            }
          }}
        />
      )}

      {/* Create Sprint Modal */}
      {showCreateSprintModal && (
        <WorkItemModal
          isOpen={showCreateSprintModal}
          onClose={() => setShowCreateSprintModal(false)}
          itemType="sprint"
          onSave={async (data) => {
            try {
              const token = authService.getTokens()?.access_token;
              if (!token) throw new Error('Authentication required');

              // Calculate default dates (2 weeks sprint)
              const startDate = new Date();
              const endDate = new Date();
              endDate.setDate(endDate.getDate() + 14);

              // Create sprint using the new sprints API
              const response = await fetch('/api/v1/work-items/sprints', {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  name: data.title || 'Sprint ' + new Date().toISOString().slice(0, 10),
                  goal: data.description || '',
                  start_date: data.start_date || startDate.toISOString().split('T')[0],
                  end_date: data.due_date || endDate.toISOString().split('T')[0],
                  capacity_points: data.custom_fields?.team_capacity || 40,
                  is_active: true // Auto-activate the sprint
                })
              });

              if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to create sprint');
              }

              const result = await response.json();
              
              setShowCreateSprintModal(false);
              await loadActiveSprint(); // Refresh to show new sprint
            } catch (error) {
              logger.error('Failed to create sprint:', error);
              throw error;
            }
          }}
        />
      )}
    </div>
  );
}
